import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Dialog } from 'radix-ui';
import { type FormEvent, type ReactNode, useState } from 'react';
import { useNavigate } from 'react-router';
import { addCustomer, addSite, getFalconProvisioning, isStatus } from '../lib/api';
import { useScope } from '../scope/scope';
import { Button } from '../ui/Button';
import { useToast } from '../ui/toast';
import { FalconBoxNote } from './Falcon';

// Owners add customers, each with its first site, and more sites (WRMM-80): an installer is always for a site.

/** Whether the signed-in person is one of this workspace's owners covering every customer, who add customers and sites. */
export function useOwnsEveryCustomer() {
  const { mspId, memberships } = useScope();
  return memberships.some(m => m.mspId === mspId && m.role === 'owner' && !m.organizationId && !m.siteId);
}
/**
 * Whether the signed-in person manages end users (WRMM-374): an MSP-wide owner, or a membership with no site holding
 * "Manage end users" for this customer (or, with none named, any customer) or every customer. The API decides; this
 * only chooses what to show.
 */
export function useManagesEndUsers(organizationId?: string) {
  const { mspId, memberships } = useScope();
  const owner = useOwnsEveryCustomer();
  return owner || memberships.some(m => m.mspId === mspId && !m.siteId && m.capabilities.includes('end_users.manage')
    && (!m.organizationId || !organizationId || m.organizationId === organizationId));
}

const field = 'h-9 w-full rounded-lg border border-line bg-panel px-2.5 text-[13.5px] outline-none focus:border-brand/50';
const problem = (error: unknown, taken: string) => isStatus(error, 409)
  ? ((error as Error).message && !(error as Error).message.startsWith('Request failed') ? (error as Error).message : taken)
  : isStatus(error, 400) ? 'Use 1 to 80 characters for each name.'
  : isStatus(error, 403) ? 'Only owners covering every customer can add customers and sites.' : 'It wasn’t added. Try again.';

export function AddCustomerDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { mspId } = useScope();
  const client = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const [name, setName] = useState(''), [site, setSite] = useState('Main');
  // WRMM-229 (Harry, 2026-10-29): the CrowdStrike box, ticked by default, only where this server can create children.
  const provisioning = useQuery({ queryKey: ['scope', mspId, 'falcon-provisioning'], queryFn: ({ signal }) => getFalconProvisioning(mspId, signal), enabled: open });
  // Saving waits for a successful answer, so neither a quick owner nor a failed lookup skips a box ticked by default.
  const shown = !!provisioning.data?.connected;
  const [falcon, setFalcon] = useState(true);
  const add = useMutation({
    mutationFn: () => addCustomer(mspId, { name, siteName: site, ...(shown ? { falcon } : {}) }),
    onSuccess: added => {
      void client.invalidateQueries({ predicate: q => q.queryKey[0] === 'scope' && q.queryKey[1] === mspId });
      onOpenChange(false);
      setName(''); setSite('Main'); setFalcon(true);
      toast({ title: `Added ${added.organization.name}`, body: `With its site ${added.site.name}. Download an installer for it in Settings → Add devices.` });
      navigate(`/w/${mspId}/customers/${added.organization.id}`);
    },
  });
  return (
    <NameDialog open={open} onOpenChange={o => { if (!add.isPending) { onOpenChange(o); add.reset(); } }} title="Add a customer"
      description="A business whose computers WITTCO looks after. Its devices are added at one of its sites."
      busy={add.isPending} error={add.isError ? problem(add.error, 'A customer with that name already exists.') : null}
      canSubmit={!!name.trim() && !!site.trim() && provisioning.isSuccess} submit="Add customer" onSubmit={() => add.mutate()}>
      <label className="block text-[13px] font-medium text-ink-2">Customer name
        <input autoFocus value={name} onChange={e => setName(e.target.value)} maxLength={80} placeholder="e.g. WITTCO" className={`${field} mt-1`} /></label>
      <label className="block text-[13px] font-medium text-ink-2">First site
        <input value={site} onChange={e => setSite(e.target.value)} maxLength={80} placeholder="e.g. Main office" className={`${field} mt-1`} /></label>
      {provisioning.isError && <p role="status" className="text-[13px] text-attention">Couldn’t check whether CrowdStrike can be set up for this
        customer. <button type="button" className="underline" onClick={() => void provisioning.refetch()}>Try again</button></p>}
      {shown && <div className="space-y-2 rounded-lg border border-line p-3">
        <label className="flex items-start gap-2 text-[13px] font-medium">
          <input type="checkbox" className="mt-0.5" checked={falcon} onChange={e => setFalcon(e.target.checked)} />CrowdStrike Falcon</label>
        <FalconBoxNote defaults={provisioning.data?.defaults ?? null} />
      </div>}
    </NameDialog>
  );
}

export function AddSiteDialog({ organizationId, customer, open, onOpenChange }: { organizationId: string; customer: string | undefined; open: boolean; onOpenChange: (open: boolean) => void }) {
  const { mspId } = useScope();
  const client = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState('');
  const add = useMutation({
    mutationFn: () => addSite(mspId, organizationId, { name }),
    onSuccess: added => {
      void client.invalidateQueries({ predicate: q => q.queryKey[0] === 'scope' && q.queryKey[1] === mspId });
      onOpenChange(false);
      setName('');
      toast({ title: `Added ${added.name}`, body: customer ? `A site of ${customer}.` : undefined });
    },
  });
  return (
    <NameDialog open={open} onOpenChange={o => { if (!add.isPending) { onOpenChange(o); add.reset(); } }} title={`Add a site${customer ? ` to ${customer}` : ''}`}
      description="A place the customer’s computers are, such as an office or a home. An installer adds devices at one site."
      busy={add.isPending} error={add.isError ? problem(add.error, 'This customer already has a site with that name.') : null}
      canSubmit={!!name.trim()} submit="Add site" onSubmit={() => add.mutate()}>
      <label className="block text-[13px] font-medium text-ink-2">Site name
        <input autoFocus value={name} onChange={e => setName(e.target.value)} maxLength={80} placeholder="e.g. Home office" className={`${field} mt-1`} /></label>
    </NameDialog>
  );
}

function NameDialog({ open, onOpenChange, title, description, busy, error, canSubmit, submit, onSubmit, children }: {
  open: boolean; onOpenChange: (open: boolean) => void; title: string; description: string; busy: boolean; error: string | null;
  canSubmit: boolean; submit: string; onSubmit: () => void; children: ReactNode;
}) {
  const send = (e: FormEvent) => { e.preventDefault(); if (canSubmit && !busy) onSubmit(); };
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 animate-fade" />
        <Dialog.Content className="fixed inset-x-0 bottom-0 z-50 max-h-[90dvh] overflow-y-auto rounded-t-2xl border border-line bg-raised p-5 shadow-pop animate-pop sm:inset-auto sm:left-1/2 sm:top-[10vh] sm:w-[min(460px,calc(100vw-2rem))] sm:-translate-x-1/2 sm:rounded-2xl">
          <form onSubmit={send}>
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <Dialog.Title className="text-[17px] font-semibold">{title}</Dialog.Title>
                <Dialog.Description className="mt-0.5 text-[13px] text-ink-2">{description}</Dialog.Description>
              </div>
              <Dialog.Close className="rounded-md p-1 text-ink-3 hover:bg-hover" aria-label="Close"><X className="size-4" /></Dialog.Close>
            </div>
            <div className="mt-4 space-y-3">{children}</div>
            {error && <p role="alert" className="mt-3 text-[13px] text-attention">{error}</p>}
            <div className="mt-5 flex justify-end gap-2">
              <Dialog.Close asChild><Button variant="ghost" disabled={busy}>Cancel</Button></Dialog.Close>
              <Button type="submit" variant="primary" disabled={!canSubmit || busy}>{busy ? 'Adding…' : submit}</Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
